import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, ConnectableObservable, of } from 'rxjs';
import { publishLast, publishReplay, tap } from 'rxjs/operators';
import { ISecureStorage } from '../../classes/interfaces/isecurestorage';

import { withSpinner } from "../../extensions/withSpinner.extension";
import { AccountCategory } from "../../types/accountcategory";
import { API_ROOT, SECURE_LOCAL_STORAGE } from "../../types/constants/appconstants";

@Injectable({
    "providedIn": "root"
})
export class AccountCategoryService {
    private http = inject(HttpClient);
    private apiRoot = inject(API_ROOT);
    private storage = inject<ISecureStorage>(SECURE_LOCAL_STORAGE);
    private accountCategoryServiceBaseUrl = this.apiRoot + "api/accountcategories";
    private storageKey: string = "AccountCategories";

    private accountCategoriesObs: ConnectableObservable<AccountCategory[]>;
    private accountCategoriesPurchaseOrdersObs: ConnectableObservable<AccountCategory[]>;
    private lastCommunityId_getAccountCategories: number;
    private lastCommunityId_getVendorAccountCategories: number;
    private lastMarketId_getAccountCategories: number;
    private lastMarketId_getVendorAccountCategories: number;
    private lastUserRoleId_getAccountCategories: number;
    private lastUserRoleId_getVendorAccountCategories: number;
    private lastVendorId_getVendorAccountCategories: number;    
    private vendorAccountCategoriesPurchaseOrdersObs: ConnectableObservable<AccountCategory[]>;

    getAccountCategories(): Observable<AccountCategory[]> {
        var cacheAccountCategories = this.storage.getItem<AccountCategory>(this.storageKey) as AccountCategory[];
        if(cacheAccountCategories && cacheAccountCategories.length > 0) {
            const acctCategories$ = of(cacheAccountCategories);
            return acctCategories$;
        }

        const result$ = withSpinner(this.http).get<AccountCategory[]>(this.accountCategoryServiceBaseUrl).pipe(
            tap((acctCategories: AccountCategory[]) => {
                var expiration = new Date();
                expiration.setHours(expiration.getHours() + 1);
                this.storage.setItem(this.storageKey, acctCategories, expiration);
            })
        );

        return result$;
    }

    getAccountCategoriesForPurchaseOrders(marketId: number = 0, communityId: number = 0, userRoleId: number = 0): Observable<AccountCategory[]> {
        if (this.lastMarketId_getAccountCategories !== marketId || this.lastCommunityId_getAccountCategories !== communityId || this.lastUserRoleId_getAccountCategories !== userRoleId) {
            this.accountCategoriesPurchaseOrdersObs = withSpinner(this.http, "Loading Account Categories...").get<AccountCategory[]>(this.accountCategoryServiceBaseUrl + "/" + marketId + "/" + communityId + "/" + 0 + "/" + userRoleId).pipe(
                publishLast()
            ) as ConnectableObservable<AccountCategory[]>;
            this.accountCategoriesPurchaseOrdersObs.connect();
            
            this.lastMarketId_getAccountCategories = marketId;
            this.lastCommunityId_getAccountCategories = communityId;
            this.lastUserRoleId_getAccountCategories = userRoleId;
        }

        return this.accountCategoriesPurchaseOrdersObs;
    }

    getVendorAccountCategoriesForPurchaseOrders(marketId: number = 0, communityId: number = 0, vendorId: number = 0, userRoleId: number = 0): Observable<AccountCategory[]> {        
        if (this.lastMarketId_getVendorAccountCategories !== marketId || this.lastCommunityId_getVendorAccountCategories !== communityId || this.lastVendorId_getVendorAccountCategories !== vendorId ||this.lastUserRoleId_getVendorAccountCategories !== userRoleId) {
            this.vendorAccountCategoriesPurchaseOrdersObs = withSpinner(this.http, "Loading Vendor Account Categories...").get<AccountCategory[]>(this.accountCategoryServiceBaseUrl + "/" + marketId + "/" + communityId + "/" + vendorId + "/" + userRoleId).pipe(
                publishLast()
            ) as ConnectableObservable<AccountCategory[]>;
            this.vendorAccountCategoriesPurchaseOrdersObs.connect();

            this.lastMarketId_getVendorAccountCategories = marketId;
            this.lastCommunityId_getVendorAccountCategories = communityId;
            this.lastVendorId_getVendorAccountCategories = vendorId;
            this.lastUserRoleId_getVendorAccountCategories = userRoleId;
        }

        return this.vendorAccountCategoriesPurchaseOrdersObs;
    }
}